import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Campagne } from '@core/models/campagne.model';
import { GlobalDataService } from './global-data.service';

@Injectable({
  providedIn: 'root'
})
export class CampagnePlanificationService {

  form = this._formBuilder.group({
    Recurrence: new FormControl(null),
    CodeVitesse: new FormControl(null),
    FrequenceEnvoi: new FormControl(null),

    DateRemiseImprimeur: new FormControl(null, [Validators.required]),

    DateTheoriqueDepotLaPoste: new FormControl({ value: null, disabled: true }),
    DateEstimeeDistribution: new FormControl({ value: null, disabled: true }),

    DetailRecurrence: new FormControl(null), // Jours de la semaine...
    DetailRecurrenceMois: new FormControl(null), // Le premier Lundi du mois / Le dernier Lundi du mois

    DateDeDebut: new FormControl(new Date(), [Validators.required]),
    DateDeFin: new FormControl(null, [Validators.required]),

  }, { validator: this.validateDate });

  constructor(
    public _GlobalDataService: GlobalDataService,
    private _formBuilder: FormBuilder,
    private _datePipe: DatePipe,
  ) {
    if (typeof this._GlobalDataService.Campagne === 'object' && this._GlobalDataService.Campagne) {

      this.form.get('Recurrence')?.patchValue(
        this._GlobalDataService.Campagne.Recurrence,
        { emitEvent: false }
      );

      this.form.get('CodeVitesse')?.patchValue(
        this._GlobalDataService.Campagne.CodeVitesse,
        { emitEvent: false }
      );

      this.form.get('FrequenceEnvoi')?.patchValue(
        this._GlobalDataService.Campagne.FrequenceEnvoi,
        { emitEvent: false }
      );

      this.form.get('DetailRecurrence')?.patchValue(
        this._GlobalDataService.Campagne.DetailRecurrence,
        { emitEvent: false }
      );

      this.form.get('DateRemiseImprimeur')?.patchValue(
        this.getValidDateOrNull(this._GlobalDataService.Campagne.DateRemiseImprimeur),
        { emitEvent: false }
      );

      this.form.get('DateDeDebut')?.patchValue(
        this.getValidDateOrNull(this._GlobalDataService.Campagne.DateDeDebut),
        { emitEvent: false }
      );

      this.form.get('DateDeFin')?.patchValue(
        this.getValidDateOrNull(this._GlobalDataService.Campagne.DateDeFin),
        { emitEvent: false }
      );

      const dateDepot = this._GlobalDataService.Campagne.DateTheoriqueDepotLaPoste;

      if (this.isValidDateValue(dateDepot)) {
        const parsedDateDepot = new Date(dateDepot);

        this.form.get('DateTheoriqueDepotLaPoste')?.patchValue(
          this._datePipe.transform(parsedDateDepot, 'dd/MM/yyyy'),
          { emitEvent: false }
        );

        this.form.get('DateEstimeeDistribution')?.patchValue(
          this._datePipe.transform(parsedDateDepot, 'dd/MM/yyyy'),
          { emitEvent: false }
        );
      } else {
        const dateRemise = this._GlobalDataService.Campagne.DateRemiseImprimeur;

        if (this.isValidDateValue(dateRemise)) {
          const dateTheorique = new Date(dateRemise);

          if (this._GlobalDataService.Campagne.FrequenceEnvoi === 'Classique') {
            dateTheorique.setDate(dateTheorique.getDate() + 12);
          } else {
            dateTheorique.setDate(dateTheorique.getDate() + 7);
          }

          this._GlobalDataService.Campagne.DateTheoriqueDepotLaPoste =
            this._datePipe.transform(dateTheorique, 'yyyy-MM-dd HH:mm:ss');

          this.form.get('DateTheoriqueDepotLaPoste')?.patchValue(
            this._datePipe.transform(dateTheorique, 'dd/MM/yyyy'),
            { emitEvent: false }
          );

          this.form.get('DateEstimeeDistribution')?.patchValue(
            this._datePipe.transform(dateTheorique, 'dd/MM/yyyy'),
            { emitEvent: false }
          );
        }
      }
    }
  }

  private isValidDateValue(value: any): boolean {
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      value === 'null' ||
      value === 'undefined'
    ) {
      return false;
    }

    const date = new Date(value);
    return !isNaN(date.getTime());
  }

  private getValidDateOrNull(value: any): Date | null {
    if (!this.isValidDateValue(value)) {
      return null;
    }

    return new Date(value);
  }

  validateDate(form: FormGroup) {
    const now = new Date();

    const dateRemiseImprimeur = form.controls['DateRemiseImprimeur'].value;
    const dateDeDebut = form.controls['DateDeDebut'].value;
    const dateDeFin = form.controls['DateDeFin'].value;

    console.debug('DateRemiseImprimeur : ' + dateRemiseImprimeur);

    if (dateRemiseImprimeur == null) {
      return { isInvalid: true };
    }

    const parsedDateRemiseImprimeur = new Date(dateRemiseImprimeur);

    if (isNaN(parsedDateRemiseImprimeur.getTime())) {
      return { isInvalid: true };
    }

    if (parsedDateRemiseImprimeur < now) {
      return { isBeforeToday: true };
    }

    console.debug('DateDeDebut : ' + dateDeDebut);

    if (dateDeDebut == null) {
      return { isInvalid: true };
    }

    const parsedDateDeDebut = new Date(dateDeDebut);

    if (isNaN(parsedDateDeDebut.getTime())) {
      return { isInvalid: true };
    }

    if (parsedDateDeDebut < now) {
      return { isBeforeToday: true };
    }

    console.debug('DateDeFin : ' + dateDeFin);

    if (dateDeFin == null) {
      return { isInvalid: true };
    }

    const parsedDateDeFin = new Date(dateDeFin);

    if (isNaN(parsedDateDeFin.getTime())) {
      return { isInvalid: true };
    }

    if (parsedDateDeFin < parsedDateDeDebut) {
      return { isBeforeStartDate: true };
    }

    return null;
  }

  refreshPlanification(campagne: Campagne) {
    this._GlobalDataService.Campagne.CodeVitesse = campagne.CodeVitesse;
    this._GlobalDataService.Campagne.FrequenceEnvoi = campagne.FrequenceEnvoi;
    this._GlobalDataService.Campagne.Recurrence = campagne.Recurrence;
    this._GlobalDataService.Campagne.DetailRecurrence = campagne.DetailRecurrence;
    this._GlobalDataService.Campagne.DateRemiseImprimeur = campagne.DateRemiseImprimeur;
    this._GlobalDataService.Campagne.DateTheoriqueDepotLaPoste = campagne.DateTheoriqueDepotLaPoste;
    this._GlobalDataService.Campagne.DateDeDebut = campagne.DateDeDebut;
    this._GlobalDataService.Campagne.DateDeFin = campagne.DateDeFin;

    this.form.get('CodeVitesse')?.patchValue(campagne.CodeVitesse, { emitEvent: false });
    this.form.get('FrequenceEnvoi')?.patchValue(campagne.FrequenceEnvoi, { emitEvent: false });
    this.form.get('Recurrence')?.patchValue(campagne.Recurrence, { emitEvent: false });
    this.form.get('DetailRecurrence')?.patchValue(campagne.DetailRecurrence, { emitEvent: false });

    this.form.get('DateRemiseImprimeur')?.patchValue(
      this.getValidDateOrNull(campagne.DateRemiseImprimeur),
      { emitEvent: false }
    );

    this.form.get('DateDeDebut')?.patchValue(
      this.getValidDateOrNull(campagne.DateDeDebut),
      { emitEvent: false }
    );

    this.form.get('DateDeFin')?.patchValue(
      this.getValidDateOrNull(campagne.DateDeFin),
      { emitEvent: false }
    );

    if (this.isValidDateValue(campagne.DateTheoriqueDepotLaPoste)) {
      const dateDepot = new Date(campagne.DateTheoriqueDepotLaPoste);

      this.form.get('DateTheoriqueDepotLaPoste')?.patchValue(
        this._datePipe.transform(dateDepot, 'dd/MM/yyyy'),
        { emitEvent: false }
      );

      this.form.get('DateEstimeeDistribution')?.patchValue(
        this._datePipe.transform(dateDepot, 'dd/MM/yyyy'),
        { emitEvent: false }
      );
    } else {
      this.form.get('DateTheoriqueDepotLaPoste')?.patchValue(null, { emitEvent: false });
      this.form.get('DateEstimeeDistribution')?.patchValue(null, { emitEvent: false });
    }
  }

  refreshPlanificationFrequenceEnvoi(campagne: Campagne) {
    this._GlobalDataService.Campagne.FrequenceEnvoi = campagne.FrequenceEnvoi;
    this._GlobalDataService.Campagne.Recurrence = campagne.Recurrence;
    this._GlobalDataService.Campagne.DetailRecurrence = campagne.DetailRecurrence;

    this.form.get('FrequenceEnvoi')?.patchValue(campagne.FrequenceEnvoi, { emitEvent: false });
    this.form.get('Recurrence')?.patchValue(campagne.Recurrence, { emitEvent: false });
    this.form.get('DetailRecurrence')?.patchValue(campagne.DetailRecurrence, { emitEvent: false });
  }

  refreshPlanificationVitesse(campagne: Campagne) {
    if (this._GlobalDataService.CampagneExt.VitessesProduit.indexOf(campagne.CodeVitesse) > -1) {
      this._GlobalDataService.Campagne.CodeVitesse = campagne.CodeVitesse;

      this.form.get('CodeVitesse')?.patchValue(
        campagne.CodeVitesse,
        { emitEvent: false }
      );
    } else if (this._GlobalDataService.CampagneExt.VitessesProduit.length > 0) {
      this._GlobalDataService.Campagne.CodeVitesse =
        this._GlobalDataService.CampagneExt.VitessesProduit[0];

      this.form.get('CodeVitesse')?.patchValue(
        this._GlobalDataService.CampagneExt.VitessesProduit[0],
        { emitEvent: false }
      );
    }
  }

  reset() {
    this.form.get('CodeVitesse')?.patchValue(
      this._GlobalDataService.Campagne.CodeVitesse,
      { emitEvent: false }
    );

    this.form.get('FrequenceEnvoi')?.patchValue(
      this._GlobalDataService.Campagne.FrequenceEnvoi,
      { emitEvent: false }
    );

    this.form.get('Recurrence')?.patchValue(
      this._GlobalDataService.Campagne.Recurrence,
      { emitEvent: false }
    );

    this.form.get('DetailRecurrence')?.patchValue(
      this._GlobalDataService.Campagne.DetailRecurrence,
      { emitEvent: false }
    );

    this.form.get('DateRemiseImprimeur')?.patchValue(
      this.getValidDateOrNull(this._GlobalDataService.Campagne.DateRemiseImprimeur),
      { emitEvent: false }
    );

    this.form.get('DateDeDebut')?.patchValue(
      this.getValidDateOrNull(this._GlobalDataService.Campagne.DateDeDebut),
      { emitEvent: false }
    );

    this.form.get('DateDeFin')?.patchValue(
      this.getValidDateOrNull(this._GlobalDataService.Campagne.DateDeFin),
      { emitEvent: false }
    );

    if (this.isValidDateValue(this._GlobalDataService.Campagne.DateRemiseImprimeur)) {
      const dateTheorique = new Date(this._GlobalDataService.Campagne.DateRemiseImprimeur);

      if (this._GlobalDataService.Campagne.FrequenceEnvoi === 'Classique') {
        dateTheorique.setDate(dateTheorique.getDate() + 12);
      } else {
        dateTheorique.setDate(dateTheorique.getDate() + 7);
      }

      this._GlobalDataService.Campagne.DateTheoriqueDepotLaPoste =
        this._datePipe.transform(dateTheorique, 'yyyy-MM-dd HH:mm:ss');

      this.form.get('DateTheoriqueDepotLaPoste')?.patchValue(
        this._datePipe.transform(dateTheorique, 'dd/MM/yyyy'),
        { emitEvent: false }
      );

      this.form.get('DateEstimeeDistribution')?.patchValue(
        this._datePipe.transform(dateTheorique, 'dd/MM/yyyy'),
        { emitEvent: false }
      );
    } else {
      this.form.get('DateTheoriqueDepotLaPoste')?.patchValue(null, { emitEvent: false });
      this.form.get('DateEstimeeDistribution')?.patchValue(null, { emitEvent: false });
    }
  }
}